import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { connectionSessionRestriction } from '../lib/chatGptConnection';

interface ActionReview {
  decision: { state: 'pending' | 'approved'; decidedAt?: string };
  previewId: string;
  operation: string;
  requestHash: string;
  revision: string;
  actor: { userId: string; teamId: string | null };
  targets: Record<string, string>;
  expiresAt: string;
  effects: string;
  before: unknown;
  changes: unknown;
  fieldDiff: Array<{ field: string; before: unknown; after: unknown }>;
}
interface ActionOutcome {
  previewId: string;
  receiptId: string;
  operation: string;
  state: 'running' | 'applied' | 'indeterminate' | 'rejected';
  workspaceId?: string | null;
  next: string;
}
export function ChatGptActionPage() {
  const { search } = useLocation();
  const id = new URLSearchParams(search).get('preview');
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const sessionKind = useAuthStore((s) => s.sessionKind);
  const supportSession = useAuthStore((s) => s.supportSession);
  const returnToKiosk = useAuthStore((s) => s.returnToKiosk);
  const restriction = connectionSessionRestriction({ sessionKind, supportSession, returnToKiosk });
  const [loaded, setLoaded] = useState<{ token: string; review: ActionReview } | null>(null);
  const review = loaded?.token === token && loaded.review.previewId === id ? loaded.review : null;
  const [recovered, setRecovered] = useState<{ token: string; result: ActionOutcome } | null>(null);
  const outcome =
    recovered?.token === token && recovered.result.previewId === id ? recovered.result : null;
  const [error, setError] = useState<string | null>(null);
  const [savedDecision, setDecision] = useState<{
    token: string;
    previewId: string;
    message: string;
  } | null>(null);
  const decision =
    savedDecision?.token === token && savedDecision.previewId === id
      ? savedDecision.message
      : review?.decision.state === 'approved'
        ? 'Already approved. Return to ChatGPT to complete this exact action.'
        : null;
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  useEffect(() => {
    setLoaded(null);
    setRecovered(null);
    setError(null);
    setDecision(null);
    if (!token || !user || restriction) return;
    if (!id || !/^[a-f0-9-]{36}$/i.test(id)) {
      setError('This action link is invalid. Request a new preview.');
      return;
    }
    let active = true;
    void api
      .get<{ data: ActionReview }>(`/mcp/actions/${encodeURIComponent(id)}`)
      .then(({ data }) => {
        if (active) setLoaded({ token, review: data.data });
      })
      .catch(async () => {
        try {
          const { data } = await api.get<{ data: ActionOutcome }>(
            `/mcp/actions/${encodeURIComponent(id)}/result`,
          );
          if (active) setRecovered({ token, result: data.data });
        } catch {
          if (active)
            setError(
              'This action is unavailable or belongs to another account. If your workspace changed, sign in again and reopen this link to recover its result before trying another action.',
            );
        }
      });
    return () => {
      active = false;
    };
  }, [token, user?.id, user?.teamId, restriction, id]);
  async function decide(approved: boolean) {
    if (
      !review ||
      review.decision.state !== 'pending' ||
      submitting.current ||
      restriction ||
      !token
    )
      return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/mcp/actions/${encodeURIComponent(review.previewId)}/decision`, {
        approved,
        requestHash: review.requestHash,
        revision: review.revision,
      });
      setDecision({
        token,
        previewId: review.previewId,
        message: approved
          ? 'Approved. Return to ChatGPT to complete this exact action.'
          : 'Declined. This action cannot be executed.',
      });
    } catch {
      setError('The decision could not be saved. Reload this page to check its current state.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  return (
    <Box component="main" sx={{ minHeight: '100vh', p: 3, display: 'grid', placeItems: 'center' }}>
      <Paper variant="outlined" sx={{ p: 3, width: '100%', maxWidth: 760 }}>
        <Stack spacing={2}>
          <Typography variant="h5" component="h1">
            Review ChatGPT action
          </Typography>
          {restriction && <Alert severity="warning">{restriction}</Alert>}
          {!token && (
            <Alert severity="info">
              Sign in to Paddiq in another tab, then reopen this action link.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {decision && <Alert severity="success">{decision}</Alert>}
          {decision &&
            review &&
            ['team_create', 'team_complete_setup'].includes(review.operation) && (
              <Button onClick={() => window.location.reload()}>Check workspace result</Button>
            )}
          {token && !restriction && !review && !outcome && !error && <CircularProgress />}
          {outcome && (
            <>
              <Alert severity={outcome.state === 'applied' ? 'success' : 'info'}>
                {outcome.state === 'applied'
                  ? 'Workspace action completed.'
                  : `Action status: ${outcome.state}.`}
              </Alert>
              <Typography>{outcome.next}</Typography>
              {outcome.workspaceId && <Typography>Workspace: {outcome.workspaceId}</Typography>}
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                Receipt: {outcome.receiptId}
              </Typography>
              {outcome.state === 'applied' ? (
                <Button component="a" href="/drivers?billing=1" variant="contained">
                  Open workspace
                </Button>
              ) : (
                <Button onClick={() => window.location.reload()}>Check result again</Button>
              )}
            </>
          )}
          {review && !decision && (
            <>
              <Typography variant="h6">{review.operation.replaceAll('_', ' ')}</Typography>
              <Typography>{review.effects}</Typography>
              <Typography variant="body2">
                Workspace: {review.actor.teamId ?? 'Personal account'}. Expires:{' '}
                {new Date(review.expiresAt).toLocaleString()}.
              </Typography>
              <Typography variant="subtitle2">Affected records</Typography>
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {JSON.stringify(review.targets, null, 2)}
              </Box>
              <Typography variant="subtitle2">Proposed changes</Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(
                  review.fieldDiff.length ? review.fieldDiff : review.changes,
                  null,
                  2,
                )}
              </Box>
              <Box component="details">
                <summary>Current state and supporting context</summary>
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    maxHeight: 360,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(review.before, null, 2)}
                </Box>
              </Box>
              <Typography variant="body2">
                Approve only if these targets, changes and effects match your request. A changed or
                expired preview requires a new decision.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" disabled={busy} onClick={() => void decide(false)}>
                  Decline
                </Button>
                <Button variant="contained" disabled={busy} onClick={() => void decide(true)}>
                  Approve this action
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
